import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Power, PackagePlus, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '@/contexts/AppContext';
import {
  useServicePlans,
  useDeactivateServicePlan,
  usePropertySubscriptions,
  useStartBilling,
  type ServicePlanWithCaps,
  type PropertySubscription,
} from '@/hooks/useServicePlans';
import { ServicePlanDialog } from './ServicePlanDialog';
import { AssignPlanDialog } from './AssignPlanDialog';

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-wt-green-primary/20 text-wt-green-primary',
  trialing: 'bg-blue-500/20 text-blue-500',
  pending: 'bg-wt-amber-primary/20 text-wt-amber-primary',
  past_due: 'bg-wt-red-primary/20 text-wt-red-primary',
  canceled: '',
};

export function ServicePlansSection() {
  const { userProfile, plants } = useApp();
  const { data: plans, isLoading } = useServicePlans();
  const { data: subscriptions, isLoading: subsLoading } = usePropertySubscriptions();
  const deactivate = useDeactivateServicePlan();
  const startBilling = useStartBilling();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editPlan, setEditPlan] = useState<ServicePlanWithCaps | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [billingTarget, setBillingTarget] = useState<PropertySubscription | null>(null);
  const [customerEmail, setCustomerEmail] = useState('');

  const canWrite = userProfile?.permission_role === 'owner' || userProfile?.permission_role === 'admin';

  // Determine the highest tier across all plants in the org
  const TIER_RANK: Record<string, number> = { monitor: 1, alert: 2, predict: 3 };
  const orgTier = plants.length > 0
    ? plants.reduce((best, p) => {
        const t = (p as Record<string, unknown>).tier as string | undefined;
        if (!t) return best;
        return (TIER_RANK[t] ?? 0) > (TIER_RANK[best] ?? 0) ? t : best;
      }, 'monitor')
    : 'predict';

  const handleEdit = (plan: ServicePlanWithCaps) => {
    setEditPlan(plan);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setEditPlan(null);
    setDialogOpen(true);
  };

  const handleDeactivate = (plan: ServicePlanWithCaps) => {
    if (plan.subscription_count > 0) {
      toast.error('Cannot deactivate a plan with active subscriptions');
      return;
    }
    deactivate.mutate(plan.id, {
      onSuccess: () => toast.success(`"${plan.name}" deactivated`),
      onError: (err) => toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown'}`),
    });
  };

  const handleStartBilling = (sub: PropertySubscription) => {
    setBillingTarget(sub);
    setCustomerEmail('');
  };

  const handleConfirmBilling = () => {
    if (!billingTarget || !customerEmail.trim()) {
      toast.error('Customer email is required');
      return;
    }
    startBilling.mutate(
      {
        subscription_id: billingTarget.id,
        property_id: billingTarget.property_id,
        customer_email: customerEmail.trim(),
      },
      {
        onSuccess: (result) => {
          setBillingTarget(null);
          if (result.checkout_url) {
            window.open(result.checkout_url, '_blank');
            toast.success('Checkout session created — share the link with your customer');
          }
        },
        onError: (err) => {
          toast.error(`Failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
        },
      },
    );
  };

  const effectivePrice = (sub: PropertySubscription) => {
    const cents = sub.price_override_cents ?? sub.plan_price_cents ?? 0;
    const suffix = sub.plan_billing_interval === 'annual' ? '/yr' : '/mo';
    return `$${(cents / 100).toFixed(2)}${suffix}`;
  };

  return (
    <div className="space-y-6">
      {/* Plans table */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Service Plans</CardTitle>
              <CardDescription>
                Define plans for your customer properties. Each plan bundles capabilities and a price.
              </CardDescription>
            </div>
            {canWrite && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setAssignOpen(true)}>
                  <PackagePlus className="w-4 h-4 mr-1.5" />
                  Assign
                </Button>
                <Button size="sm" onClick={handleCreate}>
                  <Plus className="w-4 h-4 mr-1.5" />
                  New Plan
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : !plans || plans.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[120px] text-muted-foreground">
              <p className="text-sm">No service plans yet</p>
              {canWrite && (
                <Button variant="link" size="sm" onClick={handleCreate} className="mt-2">
                  Create your first plan
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Capabilities</TableHead>
                    <TableHead className="text-center">Properties</TableHead>
                    <TableHead>Status</TableHead>
                    {canWrite && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans.map((plan) => (
                    <TableRow key={plan.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{plan.name}</span>
                          {plan.is_default && (
                            <Badge variant="secondary" className="text-[10px] bg-wt-green-primary/20 text-wt-green-primary">
                              Default
                            </Badge>
                          )}
                        </div>
                        {plan.description && (
                          <p className="text-xs text-muted-foreground mt-0.5 max-w-[200px] truncate">
                            {plan.description}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        ${(plan.price_cents / 100).toFixed(2)}
                        <span className="text-muted-foreground text-xs">
                          /{plan.billing_interval === 'annual' ? 'yr' : 'mo'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {plan.capabilities.length === 0 ? (
                            <span className="text-xs text-muted-foreground">None</span>
                          ) : (
                            plan.capabilities.slice(0, 3).map((cap) => (
                              <Badge key={cap} variant="outline" className="text-[10px]">
                                {cap.replace(/_/g, ' ')}
                              </Badge>
                            ))
                          )}
                          {plan.capabilities.length > 3 && (
                            <Badge variant="outline" className="text-[10px]">
                              +{plan.capabilities.length - 3}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center font-mono text-sm">
                        {plan.subscription_count}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={plan.is_active ? 'default' : 'secondary'}
                          className={plan.is_active ? 'bg-wt-green-primary/20 text-wt-green-primary' : ''}
                        >
                          {plan.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      {canWrite && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2"
                              onClick={() => handleEdit(plan)}
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            {plan.is_active && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                onClick={() => handleDeactivate(plan)}
                                disabled={deactivate.isPending}
                              >
                                {deactivate.isPending ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Power className="w-3.5 h-3.5" />
                                )}
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Property subscriptions */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader>
          <CardTitle className="text-lg">Property Subscriptions</CardTitle>
          <CardDescription>
            Assigned plan subscriptions and their billing status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {subsLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : !subscriptions || subscriptions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No subscriptions yet. Assign a plan to a property to get started.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    {canWrite && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions.map((sub) => (
                    <TableRow key={sub.id}>
                      <TableCell className="font-medium">{sub.property_name}</TableCell>
                      <TableCell>{sub.plan_name}</TableCell>
                      <TableCell className="font-mono text-sm">
                        {effectivePrice(sub)}
                        {sub.price_override_cents !== null && (
                          <span className="text-[10px] text-muted-foreground ml-1">(override)</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={sub.status === 'canceled' ? 'secondary' : 'default'}
                          className={STATUS_STYLES[sub.status] ?? ''}
                        >
                          {sub.status}
                        </Badge>
                        {sub.cancel_at_period_end && sub.status === 'active' && (
                          <span className="text-[10px] text-muted-foreground ml-1">cancels at period end</span>
                        )}
                      </TableCell>
                      {canWrite && (
                        <TableCell className="text-right">
                          {sub.status === 'pending' && !sub.stripe_connect_subscription_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8"
                              onClick={() => handleStartBilling(sub)}
                            >
                              <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                              Start Billing
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <ServicePlanDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        plan={editPlan}
        orgTier={orgTier}
      />

      <AssignPlanDialog
        open={assignOpen}
        onOpenChange={setAssignOpen}
        plans={plans ?? []}
      />

      {/* Start Billing dialog */}
      <Dialog open={!!billingTarget} onOpenChange={(open) => { if (!open) setBillingTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Start Billing</DialogTitle>
          </DialogHeader>
          {billingTarget && (
            <div className="space-y-4 py-2">
              <div className="text-sm space-y-1">
                <p><span className="text-muted-foreground">Property:</span> {billingTarget.property_name}</p>
                <p><span className="text-muted-foreground">Plan:</span> {billingTarget.plan_name}</p>
                <p><span className="text-muted-foreground">Price:</span> {effectivePrice(billingTarget)}</p>
              </div>
              <div className="space-y-2">
                <Label>Customer Email</Label>
                <Input
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="homeowner@example.com"
                />
                <p className="text-xs text-muted-foreground">
                  A Stripe Checkout link will be created. Share it with the customer to complete payment.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillingTarget(null)}>Cancel</Button>
            <Button onClick={handleConfirmBilling} disabled={startBilling.isPending || !customerEmail.trim()}>
              {startBilling.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Checkout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
